import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useBusinessUnit } from "../../context/BusinessUnitContext";
import { Box, VStack, Link, Tooltip } from "@chakra-ui/react";
import {
  FaTachometerAlt,
  FaCalendarAlt,
  FaCog,
  FaClipboardList,
  FaCogs,
  FaFileUpload,
  FaHandshake,
  FaPlaneDeparture,
  FaSearch,
  FaStar,
  FaUserCog,
} from "react-icons/fa";
import { isAdminRole, convertLegacyRole } from "../../constants/roles";
import { useTranslation } from "react-i18next";

const PortalSidebar = ({ isCollapsed, onMouseEnter, onMouseLeave }) => {
  const { user } = useAuth();
  const { currentRole, features, businessUnits, isConsultantPortal } = useBusinessUnit();
  const [pilotOpen, setPilotOpen] = useState(true);
  const [partnersOpen, setPartnersOpen] = useState(true);
  const { t } = useTranslation("portal");
  
  // Convert legacy roles and check if user has admin privileges
  const userRole = convertLegacyRole(user?.role);
  const buRole = convertLegacyRole(currentRole);
  const hasBusinessUnits = businessUnits && businessUnits.length > 0;
  
  // Show Settings if:
  // 1. User has admin privileges in current BU, OR
  // 2. User is a company admin with no business units
  const isAdmin = isAdminRole(buRole) || (isAdminRole(userRole) && !hasBusinessUnits);
  // Increase sidebar icon size by 50%
  const iconSize = "1.5em";

  // Corporate Azure sidebar: navy gradient surface, muted blue-grey labels,
  // mint active bar (Phase 1 locked decision). A transparent 3px left border on
  // the base keeps non-active items aligned with the active mint accent.
  const linkProps = {
    px: 3,
    py: 2,
    borderRadius: "0 10px 10px 0",
    color: "#C2D2DE",
    borderLeft: "3px solid transparent",
    _hover: { bg: "rgba(255,255,255,0.08)", color: "white", textDecoration: "none" },
    _activeLink: {
      fontWeight: "bold",
      bg: "rgba(255,255,255,0.12)",
      color: "white",
      borderLeftColor: "portal.mint",
      boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
    },
    display: "flex",
    alignItems: "center",
    gap: 3,
  };

  const sidebarSurface = {
    bgGradient: "linear(to-b, #0B1F3A 0%, #12345A 78%, #173E66 100%)",
    color: "white",
    borderRightWidth: "1px",
    borderColor: "rgba(255,255,255,0.08)",
  };

  if (isConsultantPortal) {
    return (
      <Box
        as="aside"
        w={isCollapsed ? "60px" : "220px"}
        {...sidebarSurface}
        h="100%"
        maxH="100%"
        overflowY="auto"
        transition="width 0.2s ease"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <VStack align="stretch" spacing={1} mt={2} px={isCollapsed ? 0 : 2} as="nav">
          <Tooltip label={t("consultantPortal.sidebar.dashboard")} placement="right" isDisabled={!isCollapsed}>
            <Link
              as={NavLink}
              to="/portal/consultant/dashboard"
              {...linkProps}
              justifyContent={isCollapsed ? "center" : "flex-start"}
            >
              <FaTachometerAlt size={iconSize} />
              {!isCollapsed && t("consultantPortal.sidebar.dashboard")}
            </Link>
          </Tooltip>
          <Tooltip label={t("consultantPortal.sidebar.activityReports")} placement="right" isDisabled={!isCollapsed}>
            <Link
              as={NavLink}
              to="/portal/consultant/activity-reports"
              {...linkProps}
              justifyContent={isCollapsed ? "center" : "flex-start"}
            >
              <FaClipboardList size={iconSize} />
              {!isCollapsed && t("consultantPortal.sidebar.activityReports")}
            </Link>
          </Tooltip>
          <Tooltip label={t("consultantPortal.sidebar.leaveRequests")} placement="right" isDisabled={!isCollapsed}>
            <Link
              as={NavLink}
              to="/portal/consultant/leave-requests"
              {...linkProps}
              justifyContent={isCollapsed ? "center" : "flex-start"}
            >
              <FaPlaneDeparture size={iconSize} />
              {!isCollapsed && t("consultantPortal.sidebar.leaveRequests")}
            </Link>
          </Tooltip>
          <Tooltip label={t("consultantPortal.sidebar.assignmentFeedback")} placement="right" isDisabled={!isCollapsed}>
            <Link
              as={NavLink}
              to="/portal/consultant/assignment-feedback"
              {...linkProps}
              justifyContent={isCollapsed ? "center" : "flex-start"}
            >
              <FaStar size={iconSize} />
              {!isCollapsed && t("consultantPortal.sidebar.assignmentFeedback")}
            </Link>
          </Tooltip>
          <Tooltip label={t("consultantPortal.sidebar.calendar")} placement="right" isDisabled={!isCollapsed}>
            <Link
              as={NavLink}
              to="/portal/consultant/calendar"
              {...linkProps}
              justifyContent={isCollapsed ? "center" : "flex-start"}
            >
              <FaCalendarAlt size={iconSize} />
              {!isCollapsed && t("consultantPortal.sidebar.calendar")}
            </Link>
          </Tooltip>
        </VStack>
      </Box>
    );
  }

  return (
    <Box
      as="aside"
      w={isCollapsed ? "60px" : "220px"}
      {...sidebarSurface}
      h="100%"
      maxH="100%"
      overflowY="auto"
      transition="width 0.2s ease"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Links */}
      <VStack align="stretch" spacing={1} mt={2} px={isCollapsed ? 0 : 2} as="nav">
        <Tooltip label={t("sidebar.dashboard")} placement="right" isDisabled={!isCollapsed}>
          <Link as={NavLink} to="/portal/dashboard" {...linkProps}
            justifyContent={isCollapsed ? "center" : "flex-start"}
          >
            <FaTachometerAlt size={iconSize} />
            {!isCollapsed && t("sidebar.dashboard")}
          </Link>
        </Tooltip>
        {features?.has_profile && (
          <Tooltip label={t("sidebar.asapProfile")} placement="right" isDisabled={!isCollapsed}>
            <Link as={NavLink} to="/portal/asap-profile" {...linkProps}
              justifyContent={isCollapsed ? "center" : "flex-start"}
            >
              <FaSearch size={iconSize} />
              {!isCollapsed && t("sidebar.asapProfile")}
            </Link>
          </Tooltip>
        )}
        {features?.has_cv && (
          <Tooltip label={t("sidebar.asapCV")} placement="right" isDisabled={!isCollapsed}>
            <Link as={NavLink} to="/portal/asap-cv" {...linkProps}
              justifyContent={isCollapsed ? "center" : "flex-start"}
            >
              <FaFileUpload size={iconSize} />
              {!isCollapsed && t("sidebar.asapCV")}
            </Link>
          </Tooltip>
        )}
        {features?.has_skills && (
          <Tooltip label={t("sidebar.asapSkills")} placement="right" isDisabled={!isCollapsed}>
            <Link as={NavLink} to="/portal/asap-skills" {...linkProps}
              justifyContent={isCollapsed ? "center" : "flex-start"}
            >
              <FaUserCog size={iconSize} />
              {!isCollapsed && t("sidebar.asapSkills")}
            </Link>
          </Tooltip>
        )}
        {features?.has_pilot && features?.has_pilot_license && (
          <>
            <Tooltip label={t("sidebar.asapPilot")} placement="right" isDisabled={!isCollapsed}>
              <Link
                as={NavLink}
                to={isCollapsed ? "/portal/asap-pilot/clients" : undefined}
                onClick={() => { if (!isCollapsed) setPilotOpen((v) => !v); }}
                {...linkProps}
                justifyContent={isCollapsed ? "center" : "flex-start"}
              >
                <FaCogs size={iconSize} />
                {!isCollapsed && (pilotOpen ? t("sidebar.asapPilotOpen") : t("sidebar.asapPilotClosed"))}
              </Link>
            </Tooltip>
            {!isCollapsed && pilotOpen && (
              <VStack align="stretch" spacing={1} pl={6}>
                <Link as={NavLink} to="/portal/asap-pilot/clients" {...linkProps}>
                  {!isCollapsed && t("sidebar.pilotClients")}
                </Link>
                <Link as={NavLink} to="/portal/asap-pilot/needs" {...linkProps}>
                  {!isCollapsed && t("sidebar.pilotNeeds")}
                </Link>
                <Link as={NavLink} to="/portal/asap-pilot/consultants" {...linkProps}>
                  {!isCollapsed && t("sidebar.pilotConsultants")}
                </Link>
                <Link as={NavLink} to="/portal/asap-pilot/matches" {...linkProps}>
                  {!isCollapsed && t("sidebar.pilotMatches")}
                </Link>
                <Link as={NavLink} to="/portal/asap-pilot/consultant-tracking" {...linkProps}>
                  {!isCollapsed && t("sidebar.pilotConsultantTracking")}
                </Link>
                <Link as={NavLink} to="/portal/asap-pilot/performance" {...linkProps}>
                  {!isCollapsed && t("sidebar.pilotPerformance")}
                </Link>
                <Link as={NavLink} to="/portal/asap-pilot/tasks" {...linkProps}>
                  {!isCollapsed && t("sidebar.pilotTasks")}
                </Link>
              </VStack>
            )}
          </>
        )}
        {features?.has_partners && (
          <>
            <Tooltip label={t("sidebar.asapPartners")} placement="right" isDisabled={!isCollapsed}>
              <Link
                as={NavLink}
                to={isCollapsed ? "/portal/asap-partners/sent-requests" : undefined}
                onClick={() => { if (!isCollapsed) setPartnersOpen((v) => !v); }}
                {...linkProps}
                justifyContent={isCollapsed ? "center" : "flex-start"}
              >
                <FaHandshake size={iconSize} />
                {!isCollapsed && (partnersOpen ? t("sidebar.asapPartnersOpen") : t("sidebar.asapPartnersClosed"))}
              </Link>
            </Tooltip>
            {!isCollapsed && partnersOpen && (
              <VStack align="stretch" spacing={1} pl={6}>
                <Link as={NavLink} to="/portal/asap-partners/pools-and-partners" {...linkProps}>
                  {!isCollapsed && t("sidebar.partnersPoolsAndPartners", "Pools & Partners")}
                </Link>
                <Link as={NavLink} to="/portal/asap-partners/sent-requests" {...linkProps}>
                  {!isCollapsed && t("sidebar.partnersSentRequests", "Sent Requests")}
                </Link>
                <Link as={NavLink} to="/portal/asap-partners/received-requests" {...linkProps}>
                  {!isCollapsed && t("sidebar.partnersReceivedRequests", "Received Requests")}
                </Link>
              </VStack>
            )}
          </>
        )}
        {isAdmin && (
          <Tooltip label={t("sidebar.settings")} placement="right" isDisabled={!isCollapsed}>
            <Link as={NavLink} to="/portal/settings" {...linkProps}
              justifyContent={isCollapsed ? "center" : "flex-start"}
            >
              <FaCog size={iconSize} />
              {!isCollapsed && t("sidebar.settings")}
            </Link>
          </Tooltip>
        )}
      </VStack>
    </Box>
  );
};

export default PortalSidebar; 
